import { useMemo, useState } from "react";
import Card from "../../components/common/Card";
import Alert from "../../components/common/Alert";
import Table from "../../components/common/Table";
import PageHeader from "../../components/common/PageHeader";
import Button from "../../components/common/Button";
import FilterBar from "../../components/reporting/FilterBar";
import {
  useExportFulfillmentOrderItemsMutation,
  useGetFulfillmentOrdersQuery,
} from "../../store/api";
import { formatApiDateTime } from "../../utils/dateTime";
import { downloadBlob } from "../../utils/downloadFile";

const normalizeSearchValue = (value) =>
  String(value ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");

const FulfillmentOrderItems = ({
  embedded = false,
  compact = false,
  showFilters = true,
  showDownloadButton = true,
  status: controlledStatus,
  onStatusChange,
  search: controlledSearch,
  onSearchChange,
  startDate: controlledStartDate,
  onStartDateChange,
  endDate: controlledEndDate,
  onEndDateChange,
} = {}) => {
  const [status, setStatus] = useState(controlledStatus ?? "");
  const resolvedStatus = controlledStatus ?? status;

  const [search, setSearch] = useState(controlledSearch ?? "");
  const resolvedSearch = controlledSearch ?? search;

  const [startDate, setStartDate] = useState(controlledStartDate ?? (() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
  })());
  const resolvedStartDate = controlledStartDate ?? startDate;

  const [endDate, setEndDate] = useState(controlledEndDate ?? (() => new Date())());
  const resolvedEndDate = controlledEndDate ?? endDate;

  const setStatusValue = (value) => {
    setStatus(value);
    onStatusChange && onStatusChange(value);
  };
  const setSearchValue = (value) => {
    setSearch(value);
    onSearchChange && onSearchChange(value);
  };
  const setStartDateValue = (value) => {
    setStartDate(value);
    onStartDateChange && onStartDateChange(value);
  };
  const setEndDateValue = (value) => {
    setEndDate(value);
    onEndDateChange && onEndDateChange(value);
  };

  const query = useMemo(
    () => ({
      status: resolvedStatus || undefined,
      // Keep list API usage consistent with Orders page: fetch broad results.
      search: undefined,
      from: resolvedStartDate ? resolvedStartDate.toISOString() : undefined,
      to: resolvedEndDate ? resolvedEndDate.toISOString() : undefined,
      page: 1,
      pageSize: 100,
    }),
    [resolvedStatus, resolvedStartDate, resolvedEndDate],
  );

  const {
    data: ordersData,
    isLoading,
    isError,
    error,
    refetch,
  } = useGetFulfillmentOrdersQuery(query);

  const [exportItems, { isLoading: isExporting }] =
    useExportFulfillmentOrderItemsMutation();

  const exportQuery = useMemo(
    () => ({
      status: resolvedStatus || undefined,
      search: resolvedSearch || undefined,
      from: resolvedStartDate ? resolvedStartDate.toISOString() : undefined,
      to: resolvedEndDate ? resolvedEndDate.toISOString() : undefined,
    }),
    [resolvedStatus, resolvedSearch, resolvedStartDate, resolvedEndDate],
  );

  const handleDownload = async () => {
    const result = await exportItems(exportQuery);
    if (!("data" in result)) {
      return;
    }

    const contentDisposition = result?.meta?.response?.headers?.get(
      "content-disposition",
    );
    downloadBlob({
      blob: result.data,
      filename: `fulfillment-items-${new Date().toISOString().slice(0, 10)}.xlsx`,
      contentDisposition,
    });
  };

  const orders = Array.isArray(ordersData?.orders)
    ? ordersData.orders
    : Array.isArray(ordersData)
      ? ordersData
      : [];

  const normalizedSearch = normalizeSearchValue(resolvedSearch);
  const rows = orders.flatMap((order) => {
    const lines = Array.isArray(order?.lines) ? order.lines : [];
    return lines.map((line, index) => {
      const orderNo = order.orderNumber || order.orderNo || order.orderId || "-";
      const placedAt = formatApiDateTime(order.placedAt);
      const channel =
        order.channel ||
        order.orderChannel ||
        order.channelName ||
        order.sourceChannel ||
        "-";
      const statusLabel = order.displayStatus || order.status || "-";
      const itemName = line.itemName || "Item";
      const itemId = line.itemId || "-";
      const stationCode = line.stationCode || "-";
      const notes = line.notes || "";

      const haystack = [
        orderNo,
        order.orderId,
        statusLabel,
        channel,
        placedAt,
        itemName,
        itemId,
        stationCode,
        notes,
      ]
        .map(normalizeSearchValue)
        .filter(Boolean)
        .join("|");

      if (normalizedSearch && !haystack.includes(normalizedSearch)) {
        return null;
      }

      return {
        id: `${order.orderId}-${line.orderLineId || index}`,
        orderNo,
        status: statusLabel,
        channel,
        placedAt,
        itemName,
        qty: Number(line.qty || 0),
        unitPrice: Number(line.unitPrice || 0).toFixed(2),
        lineTotal: Number(line.lineTotal || 0).toFixed(2),
      };
    });
  });

  const filteredRows = rows.filter(Boolean);

  const compactHeader = compact ? "py-1 text-[11px]" : "";
  const compactCell = compact ? "py-0.5 text-xs" : "";
  const columns = [
    { key: "orderNo", label: "Order ID", headerClassName: compactHeader, cellClassName: compactCell },
    { key: "status", label: "Status", headerClassName: compactHeader, cellClassName: compactCell },
    { key: "channel", label: "Channel", headerClassName: compactHeader, cellClassName: compactCell },
    { key: "placedAt", label: "Placed At", headerClassName: compactHeader, cellClassName: compactCell },
    { key: "itemName", label: "Order Items", headerClassName: compactHeader, cellClassName: compactCell },
    { key: "qty", label: "Qty", headerClassName: compactHeader, cellClassName: compactCell },
    { key: "unitPrice", label: "Unit Price", headerClassName: compactHeader, cellClassName: compactCell },
    { key: "lineTotal", label: "Line Total", headerClassName: compactHeader, cellClassName: compactCell },
  ];

  return (
    <div className={embedded ? "space-y-4" : "space-y-6"}>
      {!embedded && (
        <PageHeader
          title="Order Items"
          subtitle="Flattened view of fulfillment order items."
          actions={
            <Button size="sm" onClick={handleDownload} disabled={isExporting}>
              {isExporting ? "Downloading..." : "Download"}
            </Button>
          }
        />
      )}

      <Card
        className={
          compact
            ? "p-0 border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm rounded-xl overflow-hidden"
            : "space-y-4"
        }
      >
        {(showFilters || showDownloadButton) && (
          <div
            className={
              compact
                ? "flex items-center justify-end border-b border-slate-200 px-2 py-2 dark:border-slate-700"
                : "flex flex-wrap items-start justify-between gap-2"
            }
          >
          {showFilters && (
          <div className={compact ? "w-full pt-1" : "min-w-[260px] flex-1"}>
            <FilterBar
              startDate={resolvedStartDate}
              endDate={resolvedEndDate}
              onStartDateChange={setStartDateValue}
              onEndDateChange={setEndDateValue}
              showChannel={false}
              showSearch
              search={resolvedSearch}
              onSearchChange={setSearchValue}
              searchPlaceholder="Order ID / order items / station"
              showStatus
              status={resolvedStatus}
              onStatusChange={setStatusValue}
              statusOptions={[
                { value: "", label: "All" },
                { value: "IN_KITCHEN", label: "IN_KITCHEN" },
                { value: "PREPARING", label: "PREPARING" },
                { value: "CAPTURED", label: "CAPTURED" },
                { value: "PAYMENT_FAILED", label: "PAYMENT_FAILED" },
                { value: "COMPLETED", label: "COMPLETED" },
                { value: "CANCELLED", label: "CANCELLED" },
                { value: "DELIVERED", label: "DELIVERED" },
              ]}
              onRefresh={() => refetch()}
              onReset={() => {
                const now = new Date();
                setStatusValue("");
                setSearchValue("");
                setStartDateValue(
                  new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0),
                );
                setEndDateValue(now);
              }}
            />
          </div>
          )}

          {showDownloadButton && (
            <div className="shrink-0">
              <Button size="sm" onClick={handleDownload} disabled={isExporting}>
                {isExporting ? "Downloading..." : "Download"}
              </Button>
            </div>
          )}
        </div>
        )}

        <div className={compact ? "px-2 pt-0 pb-2 md:px-2.5" : ""}>
        {isLoading && (
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Loading order items...
          </p>
        )}
        {isError && (
          <Alert tone="error">
            {error?.data?.detail ||
              error?.data?.message ||
              "Failed to load order items."}
          </Alert>
        )}
        {!isLoading && !isError && (
          <Table
            columns={columns}
            rows={filteredRows}
            emptyText="No order items."
          />
        )}
        </div>
      </Card>
    </div>
  );
};

export default FulfillmentOrderItems;
